import { formatOrderPrice } from '@/trading/helpers/orders'
import { OrderSizeType } from '@/types/enums/order'
import { toOrderSizeType } from '@/utils/instrument-classification'
import { formatMoney, PLACEHOLDER } from '@/utils/presentation/formatting'

type AmountLike = {
  type?: string | null
  value?: number | null
} | null

/**
 * Display label for the "market" pricing mode. Single source of truth shared
 * across the trade-desk surfaces that show an order's pricing mode (blotter,
 * order preview, timeline, confirmation dialogs, execution composer).
 */
export const MARKET_ORDER_LABEL = 'Market'

type PriceInput = {
  price: number | null | undefined
  amountType: OrderSizeType
}

/**
 * Renders an order price for display. Branches on `amountType`:
 * - `par`: price-of-par via `formatOrderPrice` (FI behavior — 4dp minimum,
 *   up to 8dp, `$` prefix)
 * - `shares`: dollars-per-share via `formatMoney` (2dp USD)
 * - `notional`: PLACEHOLDER — mutual funds have no per-unit price
 *
 * Returns PLACEHOLDER if the price itself is nullish. Callers that need
 * special-cased text for nullish prices (e.g. "Market" for null limit
 * price on a market order) should handle that ahead of this helper.
 *
 * Callers reading `amount.type` off an `Order` should normalize via
 * `toOrderSizeType` first — the hand-written `Amount` interface is wider
 * than the enum.
 */
export function getPriceDisplay({ price, amountType }: PriceInput): string {
  if (amountType === OrderSizeType.Notional) return PLACEHOLDER
  if (typeof price !== 'number') return PLACEHOLDER
  if (amountType === OrderSizeType.Shares) {
    return formatMoney(price, { numDigits: 2, addTrailingZeros: true })
  }
  return formatOrderPrice(price)
}

type LimitPriceOrder =
  | {
      amount?: AmountLike
      limit_price?: number | null
    }
  | undefined

/**
 * Computes the "Limit price" label for the Order Preview header. Lifted
 * here from the two preview `index.tsx` files (advisor + trade-desk) per
 * the price-helper consolidation in T062.
 *
 * - Notional orders → PLACEHOLDER (MFs have no per-unit price)
 * - Non-notional with null limit_price → `marketLabel` (defaults to "Market")
 * - Otherwise → formatted price via `getPriceDisplay`
 */
export function getLimitPriceLabel(
  order: LimitPriceOrder,
  marketLabel: string = 'Market',
): string {
  if (!order) return PLACEHOLDER
  if (order.amount?.type === OrderSizeType.Notional) {
    return PLACEHOLDER
  }
  if (!order.limit_price) {
    return marketLabel
  }
  return getPriceDisplay({
    price: order.limit_price,
    amountType: toOrderSizeType(order.amount?.type),
  })
}
